import React, { useEffect, useState } from "react";
import {
  Card,
  Typography,
  FormControl,
  Box,
  Link,
  useTheme,
  TextField,
  Grid,
  Input,
  InputAdornment,
  IconButton,
  FormHelperText,
  Divider,
  Stack,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router";
import { Link as RouteLink } from "react-router-dom";
import { useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import store, { resetAllStoreStates, RootState } from "../../../store/store";
import { ISignin } from "../../../shared/models/signin";
import {
  updateUserContext,
} from "../../../shared/utils/utils";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { isValidEmail, isValidStringInput } from "../../../shared/utils/validator";
import { signInApi } from "../../../store/api/authentication";
import { setCurrentOrg, setParentOrg } from "../../../store/reducers/party";
import { ReactComponent as THSNow } from "../../../shared/assets/ths_now/THS_NOW.svg"
import "./signin.scss";
import { PAGES_URL } from "../../../shared/constants/constants";

const SignIn = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const storeAuthState = useSelector((state: RootState) => state.authentication);

  const [email, setEmail] = React.useState({
    value: "",
    isError: false,
    errorMsg: "",
  });
  const [password, setPassword] = React.useState({
    value: "",
    isError: false,
    errorMsg: "",
  });
  const [passType, setPassType] = useState<boolean>(true);

  const validateInput = (emailVal?: string, passwordval?: string) => {
    let isValid = true;
    console.log('Validating input', { emailVal, passwordval, em: isValidEmail(emailVal), pa: isValidStringInput(passwordval) });
    if(!isValidEmail(emailVal)){
      isValid = false;
      setEmail((prevState) => ({
        ...prevState,
        isError: true,
        errorMsg: "Please enter a valid email",
      }));
    }
    else{
      setEmail((prevState) => ({
        ...prevState,
        isError: false,
        errorMsg: "",
      }))
    }

    if(!isValidStringInput(passwordval)){
      isValid = false;
      setPassword((prevState) => ({
        ...prevState,
        isError: true,
        errorMsg: "Please enter password",
      }));
    }
    else {
      setPassword((prevState) => ({
        ...prevState,
        isError: false,
        errorMsg: "",
      }));
    }

    return isValid;
  };

  const handleSignin = () => {
    console.log('Login button clicked', { 
      loading: storeAuthState.loading, 
      email: email.value, 
      hasPassword: !!password.value 
    });
    
    const isValid = validateInput(email.value, password.value);

    if (!isValid) {
      console.log('Validation failed');
      return;
    }
    const reqBody: ISignin = {
      username: email.value,
      password: password.value,
    };
    store.dispatch(signInApi(reqBody)).then((res: any) => {
      if (res.payload?.statusCode === 200) {
        const party = res.payload.data.party;
        const partyPermissions: string[] = [];
        let securityGroupId = "";
        party.securityGroups.forEach((securityGroup: any) => {
          const permissions = securityGroup.permissions;
          securityGroupId = securityGroup.groupId;
          permissions.forEach((permission: any) => {
            partyPermissions.push(permission.name);
          });
        });
        const orgPartyRole = res.payload.data.orgPartyRole;
        const orgPartyIds = res.payload.data.orgPartyIds;
        const orgPartyName = res.payload.data.orgPartyName;
        const parentOrgPartyIds = res.payload.data.parentOrgPartyIds;

        // const partySubscription = res.payload.data.partySubscription;
        // const currentDate = new Date()
        // currentDate.setHours(23, 59, 59, 0);
        // const subscriptionFromDate = new Date(partySubscription?.subscription?.fromDate);
        // const subscriptionThruDate = new Date(partySubscription?.subscription?.thruDate);

        // if(partySubscription && partySubscription?.subscription?.status === "ACTIVE" && partySubscription?.subscription?.fromDate && subscriptionFromDate.getTime() <= currentDate.getTime()
        //   && (!partySubscription?.subscription?.thruDate || subscriptionThruDate.getTime() > currentDate.getTime())){
        //     let subscriptionLimit = new Date(SUBSCRIPTION_INTERVAL.FREE_TIER); // Free Tier ended on this date
        //     if(!partySubscription?.subscription?.thruDate){
        //       subscriptionLimit = calculateSubscriptionEndDate(subscriptionFromDate, partySubscription?.subscription?.planInterval)
        //     }
        //     else{
        //       subscriptionLimit = new Date(partySubscription?.subscription?.thruDate);
        //     }
        //     partySubscription.subscription.subscriptionLimit = subscriptionLimit
        //   }

        // console.log(parentOrgPartyIds)
        store.dispatch(setCurrentOrg(orgPartyIds[0]));
        store.dispatch(
          setParentOrg(
            parentOrgPartyIds?.length > 0 ? parentOrgPartyIds[0] : ""
          )
        );
        updateUserContext(
          party.roles,
          partyPermissions,
          party._id,
          party.details.name,
          party.statusId,
          orgPartyRole,
          orgPartyIds,
          orgPartyName,
          res.payload.data.authorizationToken,
          true,
          // partySubscription
        );
        navigate(PAGES_URL.HOME);
      }
    });
  };

  // resets loading state to fix the signin button loader issue
  useEffect(() => {
    resetAllStoreStates();
  }, []);

  return (
        <Card
            sx={{
              p: 4,
              width: { xs: "70vw", sm: "60vw", md: "50vw" },
              height: { xs: "60vh", sm: "60vh", md: "60vh" }
            }}
            raised
          >
            <Box display="flex" justifyContent="center" alignItems="center">
              <Typography fontWeight={400} fontSize={22} >Hello, Welcome Back!</Typography>
            </Box>
            <FormControl
              fullWidth
              sx={{ mt: 2, height: "20%" }}
              variant="standard"
            >
              <Input
                id="email"
                placeholder="Email"
                sx={{
                  height: "100%",
                  backgroundColor: "#F6F6F6",
                  border: " 1px solid #E6E6E6",
                  p: 1
                }}
                value={email.value}
                error={email.isError}
                // helperText={email.errorMsg}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail((prevState) => ({
                    ...prevState,
                    value: event.target.value,
                  }));
                }}
              />
              <FormHelperText
                error={email?.isError}
                id="component-error-text"
              >
                {email.errorMsg ?? ""}
              </FormHelperText>
            </FormControl>
            <FormControl
              fullWidth
              sx={{ mt: 2, height: "20%" }}
              variant="standard"
            >
              <Input
                id="password"
                type={passType ? "password" : "text"}
                placeholder="Password"
                error={password.isError}
                value={password.value}
                sx={{
                  height: "100%",
                  backgroundColor: "#F6F6F6",
                  border: " 1px solid #E6E6E6",
                  p: 1
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword((prevState) => ({
                    ...prevState,
                    value: event.target.value,
                  }));
                }}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setPassType(!passType)}
                      edge="end"
                    >
                      {passType ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText
                error={password?.isError}
                id="component-error-text"
              >
                {password?.errorMsg ?? ""}
              </FormHelperText>
              {storeAuthState?.error && (
                <FormHelperText error id="component-error-text">
                  Please note username is case sensitive
                </FormHelperText>
              )}
            </FormControl>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                width={1}
              >
                <Link
                  href="/forgotPassword"
                  underline="none"
                  variant="body1"
                  className="forgot-pass"
                  sx={{ cursor: "pointer" }}
                  onClick={() => {}}
                >
                  Forgot Password
                </Link>
              </Box>
            </FormControl>
            <FormControl fullWidth sx={{ mt: 2, height: "15%" }}>
              <LoadingButton
                className="login"
                loading={storeAuthState.loading}
                disabled={storeAuthState.loading}
                loadingPosition="center"
                variant="contained"
                sx={{ borderRadius: 20, fontWeight: "bold", height: "100%" }}
                onClick={handleSignin}
              >
                {storeAuthState.loading ? 'Logging in...' : 'Login'}
              </LoadingButton>
            </FormControl>

            <FormControl fullWidth sx={{ mt: 2 }}>
              <Box display='flex' justifyContent='center' alignItems='center'>
                <Typography>Don't have an account?&nbsp;</Typography>
                <Link
                  component={RouteLink}
                  to='/signup-email'
                  underline='none'
                  variant='subtitle1'
                  sx={{ cursor: 'pointer', fontWeight: 'bold' }}
                >
                  <Typography color={"#15993E"} >Sign up here</Typography>
                </Link>
              </Box>
            </FormControl>
          </Card>
  );
};

export default SignIn;
